import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  markConversationResolved,
  type ConversationDetail,
  type MarkResolvedResult,
} from '@/lib/api';
import { conversationQueryKey } from './useConversation';

interface MarkResolvedVariables {
  /** Reserved for future per-call overrides (e.g. note text). */
  note?: string;
}

/**
 * useMarkResolved — fires the Worker resolve endpoint and optimistically
 * flips the local conversation status to 'resolved' so the UI reflects the
 * action immediately.
 *
 * IRREVERSIBLE-ish: the contact gets a `pwa_resolved` tag in GHL. The caller
 * (ActionGrid / ConversationDetail) MUST gate this behind a ConfirmDialog —
 * the hook itself does not gate. If the Worker returns the stub fallback
 * (Chunk-5 endpoint not yet deployed) we still apply the optimistic update
 * so the owner can iterate against the UI in dev.
 */
export function useMarkResolved(slug: string, conversationId: string) {
  const qc = useQueryClient();
  const key = conversationQueryKey(slug, conversationId);

  return useMutation<MarkResolvedResult, Error, MarkResolvedVariables>({
    mutationFn: () =>
      markConversationResolved({ slug, conversationId }),
    onSuccess: () => {
      // Optimistic local status flip — no need to wait for a refetch.
      qc.setQueryData<ConversationDetail>(key, (curr) => {
        if (!curr) return curr;
        return { ...curr, status: 'resolved' };
      });
      // Invalidate the conversation list so the row's status badge updates.
      void qc.invalidateQueries({ queryKey: ['conversations', slug] });
    },
  });
}
